import * as Phaser from 'phaser';
import { PALETTE } from '../render/palette';

export type { PrismInputDir } from './prismDirections';
import type { PrismInputDir } from './prismDirections';

export interface PrismOptions {
  scene: Phaser.Scene;
  cellSize: number;
  gridX: number;
  gridY: number;
  initialInputDir?: PrismInputDir;
}

const ROTATION_ORDER: PrismInputDir[] = ['W', 'N', 'E', 'S'];

// Container rotation per input direction, in radians (screen coords, +y down).
// The diamond is authored with its apex (input face) on the west side; rotating
// the container CW swings the apex through N → E → S in turn.
const ROTATION_FOR_INPUT: Record<PrismInputDir, number> = {
  W: 0,
  N: Math.PI / 2,
  E: Math.PI,
  S: -Math.PI / 2,
};

export class Prism extends Phaser.GameObjects.Container {
  public inputDir: PrismInputDir;
  public slotIndex = -1;
  private readonly crystal: Phaser.GameObjects.Container;
  private readonly shimmer: Phaser.GameObjects.Graphics;

  constructor(opts: PrismOptions) {
    super(opts.scene, (opts.gridX + 0.5) * opts.cellSize, (opts.gridY + 0.5) * opts.cellSize);
    this.inputDir = opts.initialInputDir ?? 'W';

    const vertexDist = opts.cellSize * 0.4; // half-diagonal of the rendered diamond
    const dotR = opts.cellSize * 0.075;
    const inset = vertexDist * 0.86; // dot inset from the absolute vertex for visual breathing room
    const sideHalf = (vertexDist * Math.SQRT2) / 2; // half-side of the pre-rotation square

    // The crystal: an axis-aligned square + facet triangles + shimmer, all
    // rotated 45° as a group to produce the diamond silhouette.
    this.crystal = opts.scene.add.container(0, 0);
    this.crystal.setRotation(Math.PI / 4);

    const body = opts.scene.add.graphics();
    body.fillGradientStyle(
      PALETTE.PRISM_BODY_LIGHT,
      PALETTE.PRISM_BODY_LIGHT,
      PALETTE.PRISM_BODY_DARK,
      PALETTE.PRISM_BODY_DARK,
      1,
    );
    body.fillRoundedRect(-sideHalf, -sideHalf, sideHalf * 2, sideHalf * 2, 3);
    body.lineStyle(0.8, PALETTE.PRISM_STROKE, 0.95);
    body.strokeRoundedRect(-sideHalf, -sideHalf, sideHalf * 2, sideHalf * 2, 3);

    const facets = opts.scene.add.graphics();
    // Top half — light pickup.
    facets.fillStyle(0xffffff, 0.08);
    facets.fillTriangle(-sideHalf, -sideHalf, sideHalf, -sideHalf, 0, 0);
    // Bottom half — shadow.
    facets.fillStyle(0x000000, 0.18);
    facets.fillTriangle(-sideHalf, sideHalf, sideHalf, sideHalf, 0, 0);
    // Left half — subtle additional highlight.
    facets.fillStyle(0xffffff, 0.04);
    facets.fillTriangle(-sideHalf, -sideHalf, -sideHalf, sideHalf, 0, 0);

    this.shimmer = opts.scene.add.graphics();
    this.shimmer.setAlpha(0.4);
    this.shimmer.lineStyle(0.8, PALETTE.PRISM_SHIMMER, 0.6);
    this.shimmer.lineBetween(-sideHalf * 0.78, -sideHalf * 0.78, sideHalf * 0.78, sideHalf * 0.78);

    opts.scene.tweens.add({
      targets: this.shimmer,
      alpha: { from: 0.25, to: 0.7 },
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.crystal.add([body, facets, this.shimmer]);

    // Colored output dots sit OUTSIDE the rotated crystal so their positions
    // stay at the cardinal compass points regardless of the diamond rotation.
    // Default (input=W) layout:
    //   B at north vertex, G at east vertex, R at south vertex.
    const blueDot = opts.scene.add
      .circle(0, -inset, dotR, PALETTE.B)
      .setOrigin(0.5)
      .setStrokeStyle(0.6, 0x000000, 0.5);
    const greenDot = opts.scene.add
      .circle(inset, 0, dotR, PALETTE.G)
      .setOrigin(0.5)
      .setStrokeStyle(0.6, 0x000000, 0.5);
    const redDot = opts.scene.add
      .circle(0, inset, dotR, PALETTE.R)
      .setOrigin(0.5)
      .setStrokeStyle(0.6, 0x000000, 0.5);

    // Dim input-notch dot on the west apex.
    const notch = opts.scene.add.circle(-inset, 0, dotR * 0.6, 0x1f1d33, 1).setOrigin(0.5);

    this.add([this.crystal, notch, blueDot, greenDot, redDot]);
    this.applyInputRotation();
    opts.scene.add.existing(this);
  }

  /** Cycle to the next input-face orientation: W → N → E → S → W. */
  rotateNext(): void {
    const i = ROTATION_ORDER.indexOf(this.inputDir);
    this.inputDir = ROTATION_ORDER[(i + 1) % ROTATION_ORDER.length]!;
    this.applyInputRotation();
  }

  setInputDir(dir: PrismInputDir): void {
    this.inputDir = dir;
    this.applyInputRotation();
  }

  private applyInputRotation(): void {
    this.setRotation(ROTATION_FOR_INPUT[this.inputDir]);
  }
}
